import React, { useRef, useEffect } from 'react'

import { useConfiguratorContext } from '@augmatica/iframe-interface-react'

import { setIsSaveProjectToMailModalOpen, setIsSaveProjectOfferModalOpen, setViewInside } from '../../store/ui/uiSlice'

import { FULFILLED } from '../../constants/constants'

import Heading from '../Heading/Heading'
import AddToCart from '../AppHeader/ProceedButton/AddToCart'
import PriceSummary from '../PriceSummary/PriceSummary'
import DeliveryInfo from '../DeliveryInfo/DeliveryInfo'

import * as Styled from './styles'
import UserConfiguration from '../UserConfiguration/UserConfiguration'
import { useAppDispatch, useAppSelector } from '../../hooks'

interface Props {
  onRequestSaveProject: (args: { type: string }) => {
    internalSaveUuidv4: string
  }
}

const SummaryPanel: React.FC<Props> = ({ onRequestSaveProject }) => {
  const dispatch = useAppDispatch()
  const { content } = useAppSelector((state) => state.init)
  const { meta } = useAppSelector((state) => state.configResult)
  const { isSummaryPanelVisible } = useAppSelector((state) => state.ui)
  const { requestStatus_fetchPrices_aggregated } = useAppSelector((state) => state.requests.requestStatus)
  const {
    props: { loftLeft, loftRight, loftFloor },
  } = useAppSelector((state) => state.configResult) as any

  const summaryPanelRef = useRef(null)

  const configurator = useConfiguratorContext()
  useEffect(() => {
    setViewInside(false)

    configurator.setCameraView('front')
    configurator.setVisibility({ roof: true })
  }, [])

  return (
    isSummaryPanelVisible && (
      <Styled.SummaryPanel ref={summaryPanelRef} isEditItemPanelActive={isSummaryPanelVisible}>
        <Styled.EmphasizedContent>
          <Styled.PanelHeader level={1}>{content.summary_panel.design.title}</Styled.PanelHeader>
          <Styled.Text>{content.summary_panel.design.description}</Styled.Text>
          {meta && (
            <Styled.FactBox>
              <Heading level={3}>{content.summary_panel.design.facts}</Heading>
              <p>
                <b>{content.summary_panel.design.building_area}</b>
                <span>{meta.buildingArea.toString().replace('.', ',')} m²</span>
              </p>
              <p>
                <b>{content.summary_panel.design.height}</b>
                <span>{meta.height.toString().replace('.', ',')} m</span>
              </p>
              <p>
                <b>{content.summary_panel.design.snow_zone}</b>
                <span>{meta.snowZone}</span>
              </p>
              <p>
                <b>{content.summary_panel.design.number_of_doors}</b>
                <span>{meta.doors}</span>
              </p>
              <p>
                <b>{content.summary_panel.design.number_of_windows}</b>
                <span>{meta.windows}</span>
              </p>
              <p>
                <b>{content.tabs[2].title}</b>
                {loftLeft.value || loftRight.value ? (
                  <span>
                    <span>
                      {loftLeft.value
                        ? `${content.props.loft.constructionPlan.positioning.left} ${loftLeft.value.toString().replace('.', ',')} m²`
                        : ''}
                    </span>
                    <span>
                      {loftRight.value
                        ? `${content.props.loft.constructionPlan.positioning.right} ${loftRight.value.toString().replace('.', ',')} m²`
                        : ''}
                    </span>
                    <span>{loftFloor.value === 'chip' ? content.props.loft.floor.options[1].title : ''}</span>
                  </span>
                ) : (
                  <span>{content.props.gutters.options.none.title}</span>
                )}
              </p>
            </Styled.FactBox>
          )}
          <Heading level={2}>{content.summary_panel.save_project.title}</Heading>
          <Styled.Text>{content.summary_panel.save_project.description}</Styled.Text>
          <Styled.Cta
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED}
            onClick={() => {
              if (requestStatus_fetchPrices_aggregated === FULFILLED) {
                dispatch(setIsSaveProjectToMailModalOpen(true))
              }
            }}
          >
            <i className="far fa-inbox-in" aria-hidden="true" />
            {content.summary_panel.save_project.cta}

            <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </Styled.Cta>
          <Heading level={2}>{content.summary_panel.blueprints.title}</Heading>
          <Styled.Text>{content.summary_panel.blueprints.description}</Styled.Text>
          <Styled.Cta
            disabled={requestStatus_fetchPrices_aggregated !== FULFILLED}
            onClick={() => {
              if (requestStatus_fetchPrices_aggregated === FULFILLED) {
                dispatch(setIsSaveProjectOfferModalOpen(true))
              }
            }}
          >
            <i className="far fa-inbox-in" aria-hidden="true" />
            {content.summary_panel.blueprints.cta}

            <i className="fas fa-chevron-right" aria-hidden="true"></i>
          </Styled.Cta>
          <PriceSummary insideSummaryPanel />
          <Heading level={2}>{content.summary_panel.delivery_time}</Heading>
          <DeliveryInfo />
          <AddToCart onRequestSaveProject={onRequestSaveProject} insideSummaryPanel={isSummaryPanelVisible} />
        </Styled.EmphasizedContent>

        <Styled.AdditionalContent>
          <Heading level={2}>{content.summary_panel.always_included.title}</Heading>

          {content.summary_panel.always_included.items.map((item: any, index: number) => (
            <Styled.IncludeItem key={item + index}>
              <i className="fas fa-check"></i>
              <div>
                <Heading level={2}>{item.title}</Heading>
                <Styled.Text>{item.description}</Styled.Text>
              </div>
            </Styled.IncludeItem>
          ))}

          <Heading level={2}>{content.summary_panel.configuration.title}</Heading>
          <UserConfiguration />
        </Styled.AdditionalContent>

        <Styled.EmphasizedContent>
          <Heading level={2}>{content.summary_panel.contact.title}</Heading>
          <Styled.Text>{content.summary_panel.contact.description}</Styled.Text>
          <Styled.Text>{content.summary_panel.contact.telephone}</Styled.Text>
          <Styled.Email href={`mailto:${content.summary_panel.contact.email}`}>
            {content.summary_panel.contact.email}
          </Styled.Email>
          {content.summary_panel.contact.digitalMeeting.href && content.summary_panel.contact.digitalMeeting.text && (
            <Styled.Link target="_blank" href={content.summary_panel.contact.digitalMeeting.href}>
              {content.summary_panel.contact.digitalMeeting.text}
            </Styled.Link>
          )}
        </Styled.EmphasizedContent>
      </Styled.SummaryPanel>
    )
  )
}

export default SummaryPanel
